import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Valkental: the gallery film behaves like the video in valkental.com's PDP slider (see #handleSlideSelect).
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
    this.addEventListener('play', this.#handleVideoPlay, { capture: true, signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /** Videos whose sound this gallery has already decided on since their slide was last selected. */
  #soundDecided = new WeakSet();

  /**
   * Valkental: like valkental.com's PDP slider, selecting the film's slide starts it and leaving the slide pauses it.
   * Only selections by the visitor (dot, arrow, swipe) start it, not the gallery syncing back from the zoom dialog.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideSelect = (event) => {
    const { slideshow } = this.refs;
    if (!slideshow || event.target !== slideshow) return;

    const { slide, userInitiated } = event.detail;

    for (const media of slideshow.querySelectorAll('deferred-media')) {
      if (!slide.contains(media)) {
        if (media.hasAttribute('data-media-loaded')) /** @type {any} */ (media).pauseMedia();
        continue;
      }

      if (!userInitiated || !media.querySelector('template')?.content.querySelector('video')) continue;

      for (const video of media.querySelectorAll('video')) this.#soundDecided.delete(video);

      if (media.hasAttribute('data-media-loaded')) {
        /** @type {any} */ (media).playMedia();
      } else {
        /** @type {any} */ (media).loadContent(false);
      }
    }
  };

  /**
   * Valkental: a film the visitor starts (poster, dot, arrow) plays with sound like on valkental.com; the browser's
   * controls keep sound, seeking and fullscreen in the visitor's hands. Without a user activation (a swipe) browsers
   * refuse sound, so the film then keeps playing muted until the visitor turns it on. Decided once per selection, so a
   * visitor who mutes it and presses play again keeps it muted.
   *
   * @param {Event} event
   */
  #handleVideoPlay = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || !this.refs.slideshow?.contains(video)) return;
    if (this.#soundDecided.has(video)) return;

    this.#soundDecided.add(video);
    if (video.muted && navigator.userActivation?.isActive !== false) video.muted = false;
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
